<template lang="pug">
TransitionRoot(:appear="true" :show="open" as="template")
  Teleport(to="#uc-lightbox-portal")
    .uc-lightbox.flex.items-center.justify-center.inset-0.z-50.pointer-events-auto.m-auto.h-full.relative.p-4(
      :id="id"
    )
      TransitionChild(
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-100 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      )
        .absolute.inset-0.m-auto.z-0(class="bg-black/20")
      TransitionChild(
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0 scale-95"
        enter-to="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leave-from="opacity-100 scale-100"
        leave-to="opacity-0 scale-95"
      )
        UseFocusTrap.lightbox.m-auto.z-10.relative.inline-flex(
          :class="[block ? 'lightbox--block' : 'lightbox--inline', twMerge('bg-white corners overflow-hidden p-4 sm_p-8 h-auto max-h-[90vh] max-w-[90vw]', block ? 'w-[90vw] h-[90vh]' : '', lightboxStyles, classes)]"
          v-bind="attrs"
          :data-open-iframes="openIframes"
          role="dialog"
          aria-modal="true"
        ) 
          template(v-if="screen?.bg")
            uc-asset.h-full.w-full.absolute.top-0.left-0.object-cover.max-w-none(
              :src="src"
              alt=""
              autoplay="true"
              v-for="(src, index) in screen.bg.split(/ +/)"
              :opacity="`${screen['bg-opacity']?.split(/ +/)[index]}`"
            )

          //- add a nested screen class for inline lightboxes to remove vertical spacing
          .screen--no-fade(
            :class="{ 'max-w-[100ch] screen': !block }"
            ref="lightboxRef"
            tabindex="0"
          )
            button.btn.btn-icon.opacity-50.aspect-square.flex.items-center.justify-center.font-bold.text-base.w-8.h-8.absolute.top-4.right-4.z-100.bg-transparent.hover_bg-gray-100(
              @click="close"
              type="button"
              v-if="closeButton"
              :aria-label="hasTranslation('Close')"
            ) 
              uc-icon.text-gray-700(icon="theme:menu-close")
            .flex.flex-col.max-h-full.w-full(
              :class="{ 'absolute inset-0 m-auto h-full': block, 'gap-8': !block }"
            )              
              .overflow-y-auto.overflow-x-hidden.simple-scrollbar.relative.self-center.px-2.w-full.max-h-full.corners.rounded-b-none.flow-root(
                :class="{ 'h-full': block }"
                style="clip-path: inset(0 0 0 0)"
              )
                slot(:close="close")

              .self-end.p-2( :class="{ 'w-full': !largerThanSm.value }")
                slot(:close="close" name="buttons")
                  uc-button.btn.btn-primary.ripple(
                    :disabled="locked || audioLocked"
                    @click="close"
                    :class="{ 'w-full': !largerThanSm.value }"
                    data-sfx="complete"
                    text="Continue"
                  ) 

uc-preload-sfx(name="lightbox-open")
uc-preload-sfx(name="lightbox-close")

//- Note: The 'SEPARATOR' audio marker prevents lightbox audio running into the next nested screen when the yaml isn't set up optimally. Multiple 'templates' blocks in a single screen with lightbox content between them splits the text which should be contained under a single audio-marker.
//- e.g. Safeguarding (261003) screen: future_people
div(v-if="wordDoc || audioDoc")
  //- teleport content to the end of the scroll section in AppContent
  MountedTeleport(:to="`[data-teleport='${sectionId}']`" v-if="sectionId")
    .audio-marker-lightbox
      audio-marker(:name="`lightbox-${id}`")
      slot   
      audio-marker(:name="`SEPARATOR-lightbox-${id}`")
  
  //- teleport content to the end of the screen found in uc-screen
  MountedTeleport(:to="`[data-teleport='${id}']`" v-if="id")
    .audio-marker-lightbox
      audio-marker(:name="`lightbox-${id}`")
      slot
      audio-marker(:name="`SEPARATOR-lightbox-${id}`")

</template>

<script setup lang="ts">
import {
  computed,
  ref,
  unref,
  Teleport,
  useAttrs,
  watchEffect,
  nextTick,
  inject,
  watch
} from 'vue';
import useScreenAudio, {
  useChildScreenAudio,
  synthTemplatesChildId,
  type NarratableScreen
} from '../lib/useScreenAudio';
import { schemaForTemplate } from '../lib/screen-schemas/index';
import getRouter from '../lib/router';
import { twMerge } from '../lib/mergeStyles';
import { TransitionRoot, TransitionChild } from '@headlessui/vue';
import { onKeyDown, onClickOutside } from '@vueuse/core';
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component';
import { closeLightbox } from '../lib/openLightbox';
import { setCompleted } from '../lib/SCORM';
import { play, queue, stop } from '../lib/audio';
import useAudio from '../stores/useAudio';
import MountedTeleport from '../lib/MountedTeleport.vue';
import { useCompletables, useLockables } from '../stores/useLockables';
import type { UcLightbox } from '../../types/ScreenLightbox.d.ts';
import useTranslations from '../stores/useTranslations';
import useSuspend from '../lib/useSuspend';

defineOptions({ inheritAttrs: false });

// spread attributes to the .lightbox, and twMerge the classes
const {
  class: classAttr = '',
  lightboxstyles: lightboxStyles = '',
  ...attrs
} = useAttrs();
const classes = typeof classAttr === 'string' ? classAttr : '';
const audioDoc = inject('audioDoc');
const wordDoc = inject('wordDoc');
const openIframes = unref(inject('open-iframes'));
const largerThanSm = inject('largerThanSm');
const isAudioLocked = inject('isAudioLocked');
const { getAudioURL } = useAudio();
const suspend = useSuspend();
const { translations } = useTranslations();

const props = defineProps<
  UcLightbox & { locked?: boolean } & { locked?: boolean }
>();

const open = computed(
  () => getRouter()?.currentRoute.value.params.lightbox === props.id
);

const emit = defineEmits(['close']);
const contentId = computed(() => `lightboxcontent_${props.id}`);
const sectionId = computed(() => inject('sectionId') as string);

// TODO the lightbox has two IDS that get marked complete, first its internal "content ID", which controls the disabled state of the continue button and whether the lightbox itself is completed when it closes. and then the lightbox itself is marked completed when it closes, and that is the ID that can be tracked by other components to manage their completed states.

const { locked } = useLockables(contentId.value);
useCompletables(contentId);

// update content completion when opening
watchEffect(
  async () => {
    if (open.value) {
      // wait for potential completables to mount
      await nextTick();
      if (!locked.value) setCompleted(contentId.value);
    }
  },
  { flush: 'post' }
);

// update content completion when lockable status changes
watch(locked, (curr, prev) => {
  if (prev && !curr) setCompleted(contentId.value);
});

const lbSchema = schemaForTemplate('lightbox');
const screenForAudio = computed(() =>
  props.screen?.template === 'lightbox'
    ? (props.screen as NarratableScreen)
    : undefined
);
const { resolveSlot } = useScreenAudio(screenForAudio, lbSchema);
const { resolveChildIntro, audioAiJitEnabled } = useChildScreenAudio();

const audioOwnerId = computed(() => props.screen?.id ?? props.id);

function collectDomQueueAudio(): string[] {
  const els = lightboxRef.value?.querySelectorAll('[data-queue-audio]');
  if (!els?.length) return [];
  const out: string[] = [];
  els.forEach(el => {
    const src = el.getAttribute('data-queue-audio');
    if (src) out.push(src);
  });
  return out;
}

watch(
  open,
  async isOpen => {
    if (!isOpen) return;
    stop();
    await nextTick();

    const owner = audioOwnerId.value;
    const domQueue = collectDomQueueAudio();

    const screen = props.screen as NarratableScreen | undefined;
    if (
      audioAiJitEnabled.value &&
      screen?.id &&
      screen.template === 'lightbox' &&
      lbSchema
    ) {
      const shell = await resolveSlot('intro');

      const followOn: string[] = [];
      const templates = screen.templates;
      if (Array.isArray(templates)) {
        for (let i = 0; i < templates.length; i++) {
          const raw = templates[i] as NarratableScreen;
          const child = (
            raw.id ? raw : { ...raw, id: synthTemplatesChildId(screen.id, i) }
          ) as NarratableScreen;
          const u = await resolveChildIntro(child);
          if (u) followOn.push(u);
        }
      }

      if (shell) {
        await play(shell, { owner });
        followOn.forEach(u => queue(u, { owner }));
      } else if (followOn.length) {
        await play(followOn[0]!, { owner });
        followOn.slice(1).forEach(u => queue(u, { owner }));
      }
      return;
    }

    const primary =
      props.screen?.audio ||
      props.screen?.speech ||
      getAudioURL(`lightbox-${props.id}`);

    domQueue.forEach(src => queue(src, { owner }));
    if (isOpen && primary) {
      await play(primary, { owner });
    } else if (isOpen && domQueue[0]) {
      await play(domQueue[0], { owner });
    }
  },
  { flush: 'post' }
);

const close = () => {
  if (!open.value) return;
  if (!locked.value) setCompleted(props.id);
  stop();
  closeLightbox();
  emit('close');
};

const hasTranslation = (key, fallback) => {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];

  return maybeTranslated || key;
};

const audioLocked = computed(() => {
  const lockAudio = isAudioLocked.value && suspend.audioplaying;
  return lockAudio;
});

const lightboxRef = ref<HTMLElement>();
if (!props.locked) {
  onKeyDown('Escape', close);
  onClickOutside(lightboxRef, close);
}
</script>

<style>
#app .uc-lightbox {
  .fade-in {
    opacity: 1 !important;
  }

  .lightbox--inline {
    .screen,
    .wrapper,
    .screen-grid {
      @apply flex max-h-full w-auto justify-start;
    }
  }

  .section--last .screen-next-button {
    display: none;
  }

  :not(.section) > .screen .screen-next-button {
    display: none;
  }
}
</style>
